import styled from '@emotion/styled'
import _ from 'lodash'
import { useSelector } from 'react-redux'
import { api } from 'api'
import { RootState } from 'redux/store'
import {
  DATA_TYPE_KEY,
  PAGES_AEM_DATA_TYPE_KEY,
  FILE_ASSETS_AEM_DATA_TYPE_KEY,
  supportedFileTypesForFiltering,
  AEMFilters,
  DataSelectionOptions,
  folderFilterIsValid,
} from './components'
import {
  IntegrationFormManager,
  IntegrationFormField,
  IntegrationNameBuilder,
  AccountSelectionBuilder,
  DataSelectionBuilder,
  SyncScheduleBuilder,
  FiltersBuilder,
  FieldMappingBuilder,
} from '../components'
import { FilterField } from '../components/integration-form-manager/integration-form-fields/integration-filters/types'
import { Folder } from '../components/integration-form-manager/integration-form-fields/integration-filters/types'

const FullWidth = styled.div`
  width: 100%;
  & > * + * {
    margin-top: 10px;
  }
`

export const AdobeExperienceManagerInbound = ({ assignment }) => {
  const features = useSelector((state: RootState) => state.Auth.features)
  const dynamicFoldersIsEnabled = Boolean(features?.['dynamic-aem-folder-filters'])

  const getFoldersByPath = async (path = '/'): Promise<Folder[]> => {
    const response = await api.post<any, { data: Folder[] }>(`/api/v1/integrations/aem/folders`, {
      connectorId: assignment.connector_id,
      path,
    })
    return response.data || []
  }

  const integrationFieldsFilter = field =>
    _.get(field, 'fieldTypes', [])
      .map(row => row.type)
      .includes(assignment.metadata[DATA_TYPE_KEY])

  const PagesFilters: FilterField[] = [
    {
      fieldType: 'text',
      filter: assignment.metadata.folderFilter || '',
      title: 'Folder',
      filterKey: 'folderFilter',
      hasError: !folderFilterIsValid(assignment.metadata.folderFilter),
      errorMessage: 'Folder must start with /',
    },
  ]

  const PagesDynamicFilters: FilterField[] = [
    {
      fieldType: 'folder',
      filter: assignment.metadata.folderFilter || '',
      title: 'Folder',
      filterKey: 'folderFilter',
      folderConfig: {
        folderFetcher: getFoldersByPath,
      },
    },
  ]

  const AssetsFilters: FilterField[] = [
    {
      fieldType: 'dropdown',
      filter: assignment.metadata.fileFormatFilter || [],
      title: 'File Format',
      isMulti: true,
      filterOptions: supportedFileTypesForFiltering,
      filterKey: 'fileFormatFilter',
    },
    {
      fieldType: 'text',
      filter: assignment.metadata.tagsFilter || '',
      title: 'Tags',
      filterKey: 'tagsFilter',
    },
    {
      fieldType: 'text',
      filter: assignment.metadata.folderFilter || '',
      title: 'Folder',
      filterKey: 'folderFilter',
      hasError: !folderFilterIsValid(assignment.metadata.folderFilter),
      errorMessage: 'Folder must start with /',
    },
  ]

  const AssetDynamicFilters: FilterField[] = [
    {
      fieldType: 'dropdown',
      filter: assignment.metadata.fileFormatFilter || [],
      title: 'File Format',
      isMulti: true,
      filterOptions: supportedFileTypesForFiltering,
      filterKey: 'fileFormatFilter',
    },
    {
      fieldType: 'text',
      filter: assignment.metadata.tagsFilter || '',
      title: 'Tags',
      filterKey: 'tagsFilter',
    },
    {
      fieldType: 'folder',
      filter: assignment.metadata.folderFilter || '',
      title: 'Folder',
      filterKey: 'folderFilter',
      folderConfig: {
        folderFetcher: getFoldersByPath,
      },
    },
  ]

  const Filters = {
    [FILE_ASSETS_AEM_DATA_TYPE_KEY]: dynamicFoldersIsEnabled ? AssetDynamicFilters : AssetsFilters,
    [PAGES_AEM_DATA_TYPE_KEY]: dynamicFoldersIsEnabled ? PagesDynamicFilters : PagesFilters,
  }

  const integrationNameField = new IntegrationNameBuilder().build()
  const accountSelectionField = new AccountSelectionBuilder()
    .resetOnChange(true)
    .dataType(assignment.metadata[DATA_TYPE_KEY] || FILE_ASSETS_AEM_DATA_TYPE_KEY)
    .build()
  const dataSelectionField = new DataSelectionBuilder()
    .placement(2)
    .dataOptions(DataSelectionOptions)
    .defaultValue(FILE_ASSETS_AEM_DATA_TYPE_KEY)
    .metadataKey(DATA_TYPE_KEY)
    .resetIntegrationOnChange(true)
    .filtersToReset(AEMFilters)
    .build()
  const syncScheduleField = new SyncScheduleBuilder().placement(3).build()
  const filtersField = new FiltersBuilder()
    .placement(4)
    .filters(Filters[assignment.metadata[DATA_TYPE_KEY] || FILE_ASSETS_AEM_DATA_TYPE_KEY])
    .build()
  const fieldMapping = new FieldMappingBuilder()
    .placement(5)
    .integrationTitle('AEM')
    .dataType(assignment.metadata[DATA_TYPE_KEY] || FILE_ASSETS_AEM_DATA_TYPE_KEY)
    .integrationFieldsFilter(integrationFieldsFilter)
    .build()

  const AEMInboundFormFields: IntegrationFormField[] = [
    integrationNameField,
    accountSelectionField,
    dataSelectionField,
    syncScheduleField,
    filtersField,
    fieldMapping,
  ]

  return (
    <FullWidth>
      <IntegrationFormManager
        integrationFields={AEMInboundFormFields}
        integrationAssignment={assignment}
        integrationType="adobe-experience-manager-inbound"
      />
    </FullWidth>
  )
}
